// MACHINERY V2 (Stroje) — Fleet V2 group section + health header (M2.3).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.3 + §9 D4. Reference impl:
// design_handoff_machinery_v3/machinery-v2-grouped.jsx (V2GroupHeader,
// SummaryChip).
//
// >>> THIS IS THE ONLY SECTION DESIGN. <<< §9 D4's consistency rule: every
// carried grouping dimension (type / location / line / department / all) renders
// through THIS component. There is no per-dimension section variant, because the
// designer only drew one and inventing four would be exactly the divergence D4
// was written to prevent.
//
// M2.13 EXTRACTED THE HEADER to FleetGroupHeader.tsx so the V3 table could
// reuse it instead of drawing a second one. This file is unchanged below the
// header: same counts, same chips, same rows.
//
// HEALTH COUNTS COME FROM THE CONTRACT, NOT FROM TELEMETRY. The `states` map is
// built by fleetSelectors.deriveFleetStates -> deriveCardPresentation, and
// countStates buckets it here before FleetGroupHeader paints it. This component
// never sees a raw telemetry field, so the header cannot disagree with the
// badges in the rows beneath it.
//
// ONE MACHINE, ONE CHIP — INCLUDING offline+alarms. A machine that is offline
// AND carrying alarms resolves to `offline`, because the connectivity verdict
// outranks alarms in deriveCardState (the M2.1 fix, commit f1242dd7). It
// therefore counts under OFFLINE and is deliberately absent from the alarm
// count: the alarm is stale information about a machine we can no longer reach,
// and adding it to "Y alarm" would tell a supervisor to go look at a live fault
// that may have cleared hours ago. The row still SHOWS the alarm, marked
// last-known (presentation.alarmLastKnown) — filtered as offline, displayed as
// offline-with-a-last-known-alarm. `stale-alarm` (reachable, stale data, alarm
// present) keeps its own chip and is NOT folded into `alarm` either — same
// one-canonical-state-per-cell discipline as the M2.2 LiveFleetStrip.
//
// The chip vocabulary + the "always show running/idle" rule moved with the
// header — see FleetGroupHeader.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { Machine } from "../../models/models";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import FleetGroupHeader from "./FleetGroupHeader";
import FleetRow from "./FleetRow";
import { FleetMenuAction } from "./FleetCardMenu";
import { MachineryV2CardState, MachineryV2Telemetry, unconnectedTelemetry } from "./cardStates";
import { countStates } from "./fleetSelectors";

export interface FleetGroupSectionProps {
  title: string;
  machines: Machine[];
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  newMachineId: string | null;
  collapsed: boolean;
  onToggle: () => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetGroupSection: React.FC<FleetGroupSectionProps> = ({
  title, machines, states, telemetry, readiness, newMachineId, collapsed,
  onToggle, buildActions, onOpenProfile, onOpenReadiness,
}) => {
  const counts = countStates(machines, states);

  return (
    <div style={{ minWidth: 0 }}>
      <FleetGroupHeader
        title={title}
        machineCount={machines.length}
        counts={counts}
        collapsed={collapsed}
        onToggle={onToggle}
      />

      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          {machines.map((m) => (
            <FleetRow
              key={m.id}
              machine={m}
              telemetry={telemetry.get(m.id) || unconnectedTelemetry()}
              readiness={readiness.get(m.id) || null}
              isNew={m.id === newMachineId}
              actions={buildActions(m)}
              onOpenProfile={() => onOpenProfile(m)}
              onOpenReadiness={() => onOpenReadiness(m)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FleetGroupSection;
